import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { TagIcon, BrainCircuitIcon, TrendingUpIcon, SaveIcon, XIcon, SparklesIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '@/shared/services/api';
import { classificationSchema, ClassificationFormData } from './classificationApi';
import { Spinner } from '@/shared/components/Spinner';
import { ImpactBadge } from '@/shared/components/classification/ImpactBadge';
import { ComplexityBadge } from '@/shared/components/classification/ComplexityBadge';
import { cn } from '@/shared/utils/cn';
import { TagChip, TagSwatch } from '@/shared/components/TagChip';
import type { TagOption } from '@/shared/types/api';

interface ComplexityLevel {
  value: string;
  description: string;
}

interface ImpactLevel {
  value: string;
  description: string;
}

interface ClassificationOptions {
  complexityLevels: ComplexityLevel[];
  impactLevels: ImpactLevel[];
  /** The active tags, in display order (SC-376). */
  commonTags: string[];
  /** Description and colour per active tag (SC-272 / SC-376). */
  tagDescriptions?: TagOption[];
}

interface ClassificationModalProps {
  feedItemId: number;
  currentComplexity?: string;
  currentImpact?: string;
  currentTags?: string[];
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: ClassificationFormData) => Promise<void>;
}

export const ClassificationModal: React.FC<ClassificationModalProps> = ({
  currentComplexity,
  currentImpact,
  currentTags = [],
  isOpen,
  onClose,
  onSave,
}) => {
  const [options, setOptions] = useState<ClassificationOptions | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ClassificationFormData>({
    resolver: zodResolver(classificationSchema),
    defaultValues: {
      complexity: currentComplexity || '',
      potentialImpact: currentImpact || '',
      tags: currentTags || [],
    },
  });

  const watchedTags = watch('tags');
  const watchedComplexity = watch('complexity');
  const watchedImpact = watch('potentialImpact');

  useEffect(() => {
    if (!isOpen || options) return;

    const loadClassificationOptions = async () => {
      setLoading(true);
      try {
        const response = await api.get('/classification/options');
        setOptions(response.data);
      } catch (error) {
        console.error('Error loading classification options:', error);
        toast.error('Failed to load classification options');
      } finally {
        setLoading(false);
      }
    };

    loadClassificationOptions();
  }, [isOpen, options]);

  const handleTagToggle = (tag: string) => {
    const currentTagList = watchedTags || [];
    const newTags = currentTagList.includes(tag)
      ? currentTagList.filter((t) => t !== tag)
      : [...currentTagList, tag];
    setValue('tags', newTags);
  };

  const onSubmit = async (data: ClassificationFormData) => {
    setSaving(true);
    try {
      await onSave(data);
      onClose();
      toast.success('Classification saved successfully');
    } catch (error) {
      console.error('Error saving classification:', error);
      toast.error('Failed to save classification');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const tagCount = watchedTags?.length || 0;
  const tagInfo = new Map((options?.tagDescriptions ?? []).map((t) => [t.name, t]));
  // SC-376 decision 1: a removed tag already on the item is kept (it stays in the form value)
  // but shown read-only - it is not in the active list, so it cannot be toggled or re-added.
  const legacyTags = options ? currentTags.filter((t) => !options.commonTags.includes(t)) : [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/70 p-4 backdrop-blur-xs animate-fade-in">
      <div className="surface-overlay flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden shadow-modal motion-safe:animate-slide-up">
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <div className="flex items-center gap-2">
            <SparklesIcon className="h-5 w-5 text-primary" aria-hidden="true" />
            <h3 className="text-lg font-semibold text-foreground">Classify RSS Feed Item</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="btn-icon btn-ghost"
            aria-label="Close"
          >
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 space-y-6 overflow-y-auto px-6 py-6">
            {loading ? (
              <div className="flex flex-col items-center justify-center gap-3 py-12 text-muted-foreground">
                <Spinner className="h-8 w-8 text-primary" />
                <p className="text-sm">Loading classification options…</p>
              </div>
            ) : (
              <>
                <div>
                  <label className="label flex items-center gap-2">
                    <BrainCircuitIcon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                    Complexity Level
                  </label>
                  <select {...register('complexity')} className="input">
                    <option value="">Select complexity level...</option>
                    {options?.complexityLevels.map((level) => (
                      <option key={level.value} value={level.value}>
                        {level.value} - {level.description}
                      </option>
                    ))}
                  </select>
                  {watchedComplexity && (
                    <div className="mt-2">
                      <ComplexityBadge value={watchedComplexity} />
                    </div>
                  )}
                  {errors.complexity && (
                    <p className="sev sev-critical mt-2">{errors.complexity.message}</p>
                  )}
                </div>

                <div>
                  <label className="label flex items-center gap-2">
                    <TrendingUpIcon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                    Potential Impact
                  </label>
                  <select {...register('potentialImpact')} className="input">
                    <option value="">Select impact level...</option>
                    {options?.impactLevels.map((level) => (
                      <option key={level.value} value={level.value}>
                        {level.value} - {level.description}
                      </option>
                    ))}
                  </select>
                  {watchedImpact && (
                    <div className="mt-2">
                      <ImpactBadge value={watchedImpact} />
                    </div>
                  )}
                  {errors.potentialImpact && (
                    <p className="sev sev-critical mt-2">{errors.potentialImpact.message}</p>
                  )}
                </div>

                <div>
                  <label className="label flex items-center gap-2">
                    <TagIcon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                    Tags (select up to 10)
                  </label>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {options?.commonTags.map((tag) => {
                      const selected = watchedTags?.includes(tag);
                      return (
                        <button
                          key={tag}
                          type="button"
                          onClick={() => handleTagToggle(tag)}
                          aria-pressed={selected}
                          title={tagInfo.get(tag)?.description}
                          className={cn(
                            'inline-flex items-center justify-center gap-1.5 rounded-md border px-3 py-2 text-sm font-medium transition-colors',
                            selected
                              ? 'border-primary bg-primary/10 text-primary'
                              : 'border-border bg-surface text-muted-foreground hover:bg-muted hover:text-foreground'
                          )}
                        >
                          <TagSwatch color={tagInfo.get(tag)?.color} />
                          {tag}
                        </button>
                      );
                    })}
                  </div>
                  {legacyTags.length > 0 && (
                    <div className="mt-3" data-testid="legacy-tags">
                      <p className="mb-1.5 text-xs text-muted-foreground">
                        Removed tags - kept on this item, but no longer assignable:
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {legacyTags.map((tag) => (
                          <TagChip key={tag} name={tag} legacy />
                        ))}
                      </div>
                    </div>
                  )}
                  <p className="mt-2 text-sm text-muted-foreground">
                    Selected: {tagCount}/10
                  </p>
                  {errors.tags && (
                    <p className="sev sev-critical mt-2">{errors.tags.message}</p>
                  )}
                </div>
              </>
            )}
          </div>

          <div className="flex justify-end gap-3 border-t border-border px-6 py-4">
            <button type="button" onClick={onClose} className="btn-outline">
              Cancel
            </button>
            <button type="submit" disabled={saving || loading} className="btn-primary">
              {saving ? (
                <Spinner className="mr-2 h-4 w-4" />
              ) : (
                <SaveIcon className="mr-2 h-4 w-4" />
              )}
              Save Classification
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
